export type AgentRunStatus = 'active' | 'paused';

export interface AssignmentLike {
  assigned?: boolean;
  roleKey?: string;
  status?: AgentRunStatus;
  agents?: ReadonlyArray<{ roleKey: string; status: AgentRunStatus }>;
}

/**
 * Which sources the Tasks page may fetch. Unhiring deletes the assignment row, but task history
 * must survive it, so tasks load once the assignment read has resolved — hired or not. Missions
 * are current planned work: an unhired location has none, and reading them anyway would surface a
 * spurious load error or a stale paused banner, so missions keep the hired-or-paused gate.
 */
export function taskSourceGates(input: {
  assignmentResolved: boolean;
  assignment?: AssignmentLike;
}): { tasks: boolean; missions: boolean } {
  const hiredOrPaused =
    input.assignment?.assigned === true || input.assignment?.status === 'paused';
  return {
    tasks: input.assignmentResolved,
    missions: input.assignmentResolved && hiredOrPaused,
  };
}

/**
 * Which agents' mission snapshots the Tasks page reads (for the activity panels and paused state):
 * the agents assigned here that this practice may see. An assignment saved before a location could
 * hold two agents has only the flat `roleKey`; one that names no agent at all, every enabled agent.
 */
export function missionRoleKeysToRead(
  assignment: AssignmentLike | undefined,
  enabled: ReadonlySet<string> | null,
): string[] {
  if (!enabled) return [];
  const hired = hiredRoleKeys(assignment);
  // Only an assignment that names no agent at all (an old shape) falls back to every enabled agent.
  // One that names agents never widens to others: an agent switched on for the practice but not
  // hired here has no mission here to read.
  return hired === null ? [...enabled] : hired.filter((roleKey) => enabled.has(roleKey));
}

/** The agents hired (active or paused) at this location, or null when the assignment names none. */
function hiredRoleKeys(assignment: AssignmentLike | undefined): string[] | null {
  const listed = (assignment?.agents ?? []).map((agent) => agent.roleKey);
  if (listed.length > 0) return listed;
  if (assignment?.roleKey) return [assignment.roleKey];
  // A resolved "not hired" answer is an empty list, not an unknown.
  if (assignment?.assigned === false || assignment?.agents) return [];
  return null;
}

/**
 * The agents the Tasks page shows (agent filter, chips, the "All agents" set): of the agents this
 * practice may see (the rollout flags, practice-wide), only those hired at this location or with
 * work here (past tasks stay visible after un-hiring). An agent switched on for the practice but
 * never hired at this location does not appear. Same rule for every agent.
 */
export function visibleAgentRoleKeys(input: {
  assignment: AssignmentLike | undefined;
  enabled: ReadonlySet<string>;
  taskAgentTypes: Iterable<string | null | undefined>;
}): Set<string> {
  const hired = hiredRoleKeys(input.assignment);
  if (hired === null) return new Set(input.enabled);
  const here = new Set<string>(hired);
  for (const roleKey of input.taskAgentTypes) if (roleKey) here.add(roleKey);
  return new Set([...input.enabled].filter((roleKey) => here.has(roleKey)));
}

/**
 * Agents (among `candidateRoleKeys`) that are paused: their assignment is paused, or their mission
 * is paused. With no per-agent list, the flat assignment status applies to every candidate.
 */
export function pausedRoleKeys(input: {
  candidateRoleKeys: readonly string[];
  assignment: AssignmentLike;
  missionStatusByRole: ReadonlyMap<string, string | undefined>;
}): Set<string> {
  const perAgent = new Map((input.assignment.agents ?? []).map((a) => [a.roleKey, a.status]));
  return new Set(
    input.candidateRoleKeys.filter((roleKey) => {
      const status =
        perAgent.get(roleKey) ?? (perAgent.size === 0 ? input.assignment.status : undefined);
      return status === 'paused' || input.missionStatusByRole.get(roleKey) === 'paused';
    }),
  );
}

/**
 * RF S5: the banner claims a pause only when every HIRED agent currently shown is paused. An agent
 * that is switched on for the practice but not hired here has no assignment and must not keep the
 * banner away. With no per-agent list (older assignment), every shown agent counts as hired.
 */
export function bannerPausedRoleKeys(input: {
  shownRoleKeys: readonly string[];
  assignment: AssignmentLike;
  paused: ReadonlySet<string>;
}): string[] {
  const hired = new Set((input.assignment.agents ?? []).map((a) => a.roleKey));
  const shownHired =
    hired.size === 0 ? [...input.shownRoleKeys] : input.shownRoleKeys.filter((k) => hired.has(k));
  if (shownHired.length === 0) return [];
  return shownHired.every((roleKey) => input.paused.has(roleKey)) ? shownHired : [];
}
